import { type DataInvalidationScope } from '@gb-ticket/shared-types';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import {
  queryKeysForInvalidation,
  setDialogInvalidationHold,
  setProfileInvalidationHold,
} from '@/platform/lib/data-invalidation';

export function invalidateScope(queryClient: QueryClient, scope: DataInvalidationScope): void {
  for (const queryKey of queryKeysForInvalidation(scope)) {
    void queryClient.invalidateQueries({ queryKey });
  }
}

/** While a create/edit dialog is open, user and organisation lists wait. */
export function useDialogInvalidationHold(open: boolean): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    for (const scope of setDialogInvalidationHold(open)) {
      invalidateScope(queryClient, scope);
    }
    return () => {
      if (!open) {
        return;
      }
      for (const scope of setDialogInvalidationHold(false)) {
        invalidateScope(queryClient, scope);
      }
    };
  }, [open, queryClient]);
}

/** Unsaved profile fields wait until save or leaving the page. */
export function useProfileInvalidationHold(dirty: boolean): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    for (const scope of setProfileInvalidationHold(dirty)) {
      invalidateScope(queryClient, scope);
    }
    return () => {
      if (!dirty) {
        return;
      }
      for (const scope of setProfileInvalidationHold(false)) {
        invalidateScope(queryClient, scope);
      }
    };
  }, [dirty, queryClient]);
}
